/**
 * Transaction process graph for lead requests with negotiable price adjustments
 */

/**
 * Transitions
 */
export const transitions = {
  REQUEST: 'transition/request',
  
  // NEW: Update price adjustments without changing state
  // This allows for back-and-forth negotiation
  UPDATE_PRICE_ADJUSTMENTS: 'transition/update-price-adjustments',
  
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',
  CANCEL: 'transition/cancel',
  CANCEL_BY_CUSTOMER: 'transition/cancel-by-customer',
  CONFIRM_BOOKING: 'transition/confirm-booking',
};

/**
 * States
 */
export const states = {
  INITIAL: 'initial',
  REQUESTED: 'requested',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  CANCELLED: 'cancelled',
  BOOKING_CONFIRMED: 'booking-confirmed',
};

/**
 * Transaction process graph
 */
export const graph = {
  id: 'request-lead/release-1',
  initial: states.INITIAL,

  states: {
    [states.INITIAL]: {
      on: {
        [transitions.REQUEST]: states.REQUESTED,
      },
    },

    [states.REQUESTED]: {
      on: {
        // NEW: Update adjustments while staying in requested state
        [transitions.UPDATE_PRICE_ADJUSTMENTS]: states.REQUESTED,
        
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.DECLINE]: states.DECLINED,
        [transitions.CANCEL]: states.CANCELLED,
        [transitions.CANCEL_BY_CUSTOMER]: states.CANCELLED,
      },
    },

    [states.ACCEPTED]: {
      on: {
        [transitions.CONFIRM_BOOKING]: states.BOOKING_CONFIRMED,
      },
    },

    // Final states
    [states.DECLINED]: { type: 'final' },
    [states.CANCELLED]: { type: 'final' },
    [states.BOOKING_CONFIRMED]: { type: 'final' },
  },
};

/**
 * Check if a transition should be shown in activity feed
 */
export const isRelevantPastTransition = transition => {
  return [
    transitions.UPDATE_PRICE_ADJUSTMENTS, // NEW: Show adjustment updates
    transitions.ACCEPT,
    transitions.DECLINE,
    transitions.CANCEL,
    transitions.CANCEL_BY_CUSTOMER,
    transitions.CONFIRM_BOOKING,
  ].includes(transition);
};

/**
 * Check if transition is a customer action
 */
export const isCustomerAction = transition => {
  return [
    transitions.REQUEST,
    transitions.CANCEL_BY_CUSTOMER,
    transitions.CONFIRM_BOOKING,
  ].includes(transition);
};

/**
 * Check if transition is a provider action
 */
export const isProviderAction = transition => {
  return [
    transitions.UPDATE_PRICE_ADJUSTMENTS, // NEW: Provider can update adjustments
    transitions.ACCEPT,
    transitions.DECLINE,
  ].includes(transition);
};

/**
 * Check if transition is a system action
 */
export const isSystemAction = transition => {
  return [
    transitions.CANCEL,
  ].includes(transition);
};

/**
 * Check if transition is privileged (needs backend)
 */
export const isPrivileged = transition => {
  return [
    transitions.CONFIRM_BOOKING,
  ].includes(transition);
};

/**
 * Check when lead process is completed
 */
export const isCompleted = transition => {
  const leadCompletedTransitions = [
    transitions.ACCEPT,
    transitions.DECLINE,
    transitions.CANCEL,
    transitions.CANCEL_BY_CUSTOMER,
    transitions.CONFIRM_BOOKING,
  ];
  return leadCompletedTransitions.includes(transition);
};

/**
 * Check when lead was successful
 */
export const isSuccessful = transition => {
  return transition === transitions.ACCEPT;
};

/**
 * Check when lead was unsuccessful
 */
export const isUnsuccessful = transition => {
  const unsuccessfulTransitions = [
    transitions.DECLINE,
    transitions.CANCEL,
    transitions.CANCEL_BY_CUSTOMER,
  ];
  return unsuccessfulTransitions.includes(transition);
};

/**
 * States that need provider attention
 */
export const statesNeedingProviderAttention = [states.REQUESTED];

/**
 * States that need customer attention
 */
export const statesNeedingCustomerAttention = [states.ACCEPTED];

/**
 * Get user-friendly status message
 */
export const getLeadStatusMessage = (lastTransition, userRole = 'customer') => {
  switch (lastTransition) {
    case transitions.REQUEST:
      return userRole === 'customer' 
        ? 'Lead request sent - waiting for provider response'
        : 'New lead request received - please review and respond';
    
    case transitions.UPDATE_PRICE_ADJUSTMENTS: // NEW
      return userRole === 'customer'
        ? 'Provider updated the pricing - please review'
        : 'Price adjustments updated - customer will be notified';
    
    case transitions.ACCEPT:
      return userRole === 'customer'
        ? 'Great! Your request was accepted - you can now confirm your booking'
        : 'Lead accepted - waiting for customer to confirm booking';
    
    case transitions.DECLINE:
      return userRole === 'customer'
        ? 'Request was declined - you can try other providers or different dates'
        : 'Lead declined - the customer has been notified';
    
    case transitions.CANCEL:
      return 'This time slot is no longer available';
    
    case transitions.CANCEL_BY_CUSTOMER:
      return userRole === 'customer'
        ? 'Lead request cancelled'
        : 'Customer cancelled their lead request';

    case transitions.CONFIRM_BOOKING:
      return userRole === 'customer'
        ? 'Booking confirmed! Payment completed successfully'
        : 'Customer confirmed booking and completed payment';
    
    default:
      return 'Unknown status';
  }
};

/**
 * Get next possible actions for a transaction state
 */
export const getPossibleActions = (lastTransition, userRole) => {
  const state = getStateFromTransition(lastTransition);
  
  switch (state) {
    case states.REQUESTED:
      if (userRole === 'provider') {
        return [
          { 
            transition: transitions.UPDATE_PRICE_ADJUSTMENTS, 
            label: 'Update Pricing', 
            type: 'secondary' 
          },
          { 
            transition: transitions.ACCEPT, 
            label: 'Accept Lead', 
            type: 'primary' 
          },
          { 
            transition: transitions.DECLINE, 
            label: 'Decline', 
            type: 'secondary' 
          },
        ];
      } else if (userRole === 'customer') {
        return [
          { 
            transition: transitions.CANCEL_BY_CUSTOMER, 
            label: 'Cancel Request', 
            type: 'secondary' 
          },
        ];
      }
      return [];
    
    case states.ACCEPTED:
      if (userRole === 'customer') {
        return [
          { 
            transition: transitions.CONFIRM_BOOKING, 
            label: 'Confirm Booking', 
            type: 'primary' 
          },
        ];
      }
      return [];
    
    case states.DECLINED:
    case states.CANCELLED:
    case states.BOOKING_CONFIRMED:
      return [];
    
    default:
      return [];
  }
};

/**
 * Helper to determine current state from last transition
 */
export const getStateFromTransition = (lastTransition) => {
  switch (lastTransition) {
    case transitions.REQUEST:
    case transitions.UPDATE_PRICE_ADJUSTMENTS: // NEW: Still in requested state
      return states.REQUESTED;
    case transitions.ACCEPT:
      return states.ACCEPTED;
    case transitions.DECLINE:
      return states.DECLINED;
    case transitions.CANCEL:
    case transitions.CANCEL_BY_CUSTOMER:
      return states.CANCELLED;
    case transitions.CONFIRM_BOOKING:
      return states.BOOKING_CONFIRMED;
    default:
      return states.INITIAL;
  }
};

/**
 * Check if lead process should show messaging interface
 */
export const shouldShowMessaging = (lastTransition) => {
  return ![transitions.CANCEL, transitions.CANCEL_BY_CUSTOMER].includes(lastTransition);
};

/**
 * NEW: Check if price adjustments can be edited
 */
export const canEditPriceAdjustments = (lastTransition, userRole) => {
  const state = getStateFromTransition(lastTransition);
  return state === states.REQUESTED && userRole === 'provider';
};

/**
 * NEW: Check if transaction has price adjustments
 */
export const hasPriceAdjustments = (transaction) => {
  const adjustments = transaction?.attributes?.protectedData?.priceAdjustments;
  return Array.isArray(adjustments) && adjustments.length > 0;
};

/**
 * NEW: Calculate total with adjustments
 */
export const calculateTotalWithAdjustments = (transaction) => {
  const baseAmount = transaction?.attributes?.lineItems?.reduce(
    (sum, item) => sum + item.lineTotal.amount,
    0
  ) || 0;

  const adjustments = transaction?.attributes?.protectedData?.priceAdjustments || [];
  const adjustmentTotal = adjustments.reduce((sum, adj) => sum + adj.amount, 0);

  return {
    baseAmount,
    adjustmentTotal,
    finalTotal: baseAmount + adjustmentTotal,
    adjustments,
  };
};

/**
 * Get CSS class for transaction status styling
 */
export const getStatusClass = (lastTransition) => {
  switch (lastTransition) {
    case transitions.REQUEST:
    case transitions.UPDATE_PRICE_ADJUSTMENTS: // NEW
      return 'status-pending';
    case transitions.ACCEPT:
      return 'status-success';
    case transitions.DECLINE:
      return 'status-declined';
    case transitions.CANCEL:
    case transitions.CANCEL_BY_CUSTOMER:
      return 'status-cancelled';
    case transitions.CONFIRM_BOOKING:
      return 'status-completed';
    default:
      return 'status-unknown';
  }
};

// Legacy function exports for compatibility
export const isCustomerReview = () => false;
export const isProviderReview = () => false;
export const isRefunded = () => false;
export const isCancelled = transition => {
  return [transitions.CANCEL, transitions.CANCEL_BY_CUSTOMER].includes(transition);
};
